import { ThemeOptions, SimplePaletteColorOptions } from "@mui/material";

import { CTA_HEIGHT } from "@APP/styles/cta";

import palette from "./palette";

// Due to issues with runtime type checking causing errors when using
// palette.primary.main / palette.secondary.main from the MUI type, redefined here.
const palettePrimary = palette.primary as SimplePaletteColorOptions;

/**
 * Lloyds (LBG) component overrides.
 *
 * Buttons: solid black contained CTAs and outlined black secondary CTAs, both
 * with an ~8px radius and no shadow, matching the approved LBG designs. White
 * inputs/cards on the grey page background.
 */
const components: ThemeOptions["components"] = {
  MuiButton: {
    styleOverrides: {
      root: {
        // The LBG designs use 18px CTA labels with 28px horizontal padding.
        fontSize: 18,
        fontWeight: 600,
        minWidth: 140,
        borderRadius: 8,
        boxShadow: "none",
      },
      // Every CTA — contained or outlined, mobile or desktop — is the same
      // 48px tall. The height is pinned here rather than at the call sites so
      // it cannot drift between screens; vertical padding is dropped so the
      // label's line box can't push a button past it. Text buttons (the
      // inline "View details" / "Contact" links) are deliberately excluded.
      contained: {
        minHeight: CTA_HEIGHT,
        padding: "0 1.75rem",
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
        "&:focus": {
          boxShadow: "none",
        },
      },
      outlined: {
        minHeight: CTA_HEIGHT,
        padding: "0 1.75rem",
        borderWidth: 1.5,
        "&:hover": {
          borderWidth: 1.5,
        },
      },
      text: {
        fontSize: 14,
        color: palette.text?.primary,
      },
      textSecondary: {
        backgroundColor: "transparent",
        fontSize: 14,
        textDecoration: "underline",
        "&:hover": {
          backgroundColor: "transparent",
          textDecoration: "underline",
        },
      },
      sizeLarge: {
        fontSize: "1.125rem",
        // Same 48px as every other CTA — `sizeLarge` is applied after
        // `contained`, so it has to repeat the sizing rather than inherit it.
        minHeight: CTA_HEIGHT,
        padding: "0 1.75rem",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        fontSize: 14,
      },
    },
  },
  // The white fill belongs on the input itself, not on the TextField wrapper —
  // on the wrapper it paints a square backdrop that shows through behind the
  // rounded outline.
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: palette.background?.paper,
      },
      input: {
        fontSize: 14,
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: palette.text?.primary,
        fontWeight: 600,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        // The designs keep the bank rows white throughout; selection is shown
        // with a border, so the default grey hover/selected fill is removed.
        "&:hover, &.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: palette.background?.paper,
        },
        "&:focus-visible, &:focus-visible:hover": {
          outline: `2px solid ${palettePrimary.main}`,
          outlineOffset: "-2px",
          backgroundColor: "#FFFFFF",
        },
      },
    },
  },
};

export default components;
